import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  CreditCard, 
  Calendar, 
  CheckCircle2, 
  XCircle, 
  RefreshCw,
  Clock,
  AlertCircle
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const MySubscription = () => {
  const { user } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/my-subscription`, {
        withCredentials: true
      });
      setSubscriptionData(response.data);
    } catch (error) {
      toast.error("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/my-subscription/cancel`, {}, {
        withCredentials: true
      });
      toast.success("Subscription will be cancelled at the end of your billing period");
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/my-subscription/reactivate`, {}, {
        withCredentials: true
      });
      toast.success("Subscription reactivated!");
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate subscription");
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="my-subscription-page">
        <div>
          <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
            My Subscription
          </h1>
          <p className="text-stone-600 mt-2">Manage your subscription and billing</p>
        </div>

        {!subscriptionData?.has_subscription ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CreditCard className="h-16 w-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-stone-900 mb-2">No Active Subscription</h3>
              <p className="text-stone-600 mb-6">
                You don't have an active subscription. Subscribe to get access to premium features.
              </p>
              <Button 
                onClick={() => window.location.href = '/register'}
                className="bg-violet-700 hover:bg-violet-800"
                data-testid="subscribe-btn"
              >
                View Plans
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Current Plan Card */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl">Current Plan</CardTitle>
                    <CardDescription>Your active subscription details</CardDescription>
                  </div>
                  <Badge 
                    className={
                      subscriptionData.subscription?.cancel_at_period_end
                        ? "bg-orange-100 text-orange-700"
                        : subscriptionData.subscription?.status === 'active'
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }
                  >
                    {subscriptionData.subscription?.cancel_at_period_end
                      ? "Cancelling"
                      : subscriptionData.subscription?.status || "Unknown"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-6 bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl border border-violet-100">
                  <h3 className="text-2xl font-bold text-stone-900 mb-2">
                    {subscriptionData.subscription?.plan?.name || "Premium Plan"}
                  </h3>
                  <p className="text-stone-600 mb-4">
                    {subscriptionData.subscription?.plan?.description || "Access to all premium features"}
                  </p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-violet-700">
                      {formatCurrency(subscriptionData.subscription?.plan?.price)}
                    </span>
                    <span className="text-stone-500">
                      /{subscriptionData.subscription?.plan?.billing_cycle || 'month'}
                    </span>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex items-center gap-3 p-4 bg-stone-50 rounded-lg">
                    <Calendar className="h-5 w-5 text-violet-600" />
                    <div>
                      <p className="text-sm text-stone-500">Started</p>
                      <p className="font-medium text-stone-900">
                        {formatDate(subscriptionData.subscription?.start_date)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-stone-50 rounded-lg">
                    <Clock className="h-5 w-5 text-violet-600" />
                    <div>
                      <p className="text-sm text-stone-500">Next Billing</p>
                      <p className="font-medium text-stone-900">
                        {formatDate(subscriptionData.subscription?.next_billing_date) || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Features */}
                {subscriptionData.subscription?.plan?.features?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-stone-900 mb-3">Plan Features</h4>
                    <ul className="space-y-2">
                      {subscriptionData.subscription.plan.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2 text-stone-600">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Cancellation Notice */}
                {subscriptionData.subscription?.cancel_at_period_end && (
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-orange-800">Subscription Cancelling</p>
                      <p className="text-sm text-orange-600">
                        Your subscription will end on your next billing date. You'll still have access until then.
                      </p>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  {subscriptionData.subscription?.cancel_at_period_end ? (
                    <Button
                      onClick={handleReactivateSubscription}
                      disabled={actionLoading}
                      className="bg-violet-700 hover:bg-violet-800"
                      data-testid="reactivate-btn"
                    >
                      {actionLoading ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      Reactivate Subscription
                    </Button>
                  ) : (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          data-testid="cancel-btn"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Cancel Subscription
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Your subscription will remain active until the end of your current billing period.
                            You won't be charged again, but you'll lose access to premium features after that date.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleCancelSubscription}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Yes, Cancel
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Payment History */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Payment History</CardTitle>
                <CardDescription>Recent subscription payments</CardDescription>
              </CardHeader>
              <CardContent>
                {subscriptionData.payment_history?.length > 0 ? (
                  <ul className="space-y-3">
                    {subscriptionData.payment_history.map((payment, index) => (
                      <li 
                        key={index}
                        className="flex items-center justify-between p-3 bg-stone-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-stone-900">
                            {formatCurrency(payment.amount)}
                          </p>
                          <p className="text-sm text-stone-500">
                            {formatDate(payment.created_at)}
                          </p>
                        </div>
                        <Badge 
                          className={
                            payment.payment_status === 'paid'
                              ? "bg-green-100 text-green-700"
                              : "bg-stone-100 text-stone-700"
                          }
                        >
                          {payment.payment_status}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-stone-500 text-center py-8">No payment history</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MySubscription;
